import { useCountUp } from '../../../hooks/useCountUp';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface MetricCardProps {
  prefix?: string;
  value: number;
  suffix: string;
  label: string;
  decimals?: number;
  icon: string;
  description: string;
  duration?: number;
}

const MetricCard = ({
  prefix = '',
  value,
  suffix,
  label,
  decimals = 0,
  icon,
  description,
  duration = 2200,
}: MetricCardProps) => {
  const { count, ref } = useCountUp({ end: value, decimals, duration });

  return (
    <div
      ref={ref}
      className="group flex flex-col items-center text-center p-8 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/40 transition-all duration-300 cursor-default"
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-600/20 border border-amber-500/30 mb-5 group-hover:scale-110 transition-transform duration-300">
        <i className={`${icon} text-xl text-amber-400`} />
      </div>
      <div className="font-display text-4xl lg:text-5xl font-semibold text-white mb-1 tabular-nums">
        {prefix}
        {decimals > 0 ? count.toFixed(decimals) : count.toLocaleString('es-ES')}
        {suffix}
      </div>
      <div className="font-body text-sm font-semibold text-amber-400 tracking-wide uppercase mb-3">
        {label}
      </div>
      <div className="font-body text-xs text-white/40 leading-relaxed max-w-[180px]">
        {description}
      </div>
    </div>
  );
};

const metrics = [
  {
    prefix: '+',
    value: 3,
    suffix: 'M',
    label: 'Usuarios activos',
    icon: 'ri-user-heart-line',
    description: 'Personas que monitorean su salud diariamente con HELIX',
    duration: 1800,
  },
  {
    prefix: '',
    value: 99.2,
    suffix: '%',
    label: 'Precisión cardíaca',
    decimals: 1,
    icon: 'ri-heart-pulse-line',
    description: 'Validado clínicamente frente a equipos de grado médico certificados',
    duration: 2400,
  },
  {
    prefix: '',
    value: 7,
    suffix: ' días',
    label: 'Batería continua',
    icon: 'ri-battery-2-charge-line',
    description: 'Con monitoreo 24/7 activo. El doble que cualquier competidor directo',
    duration: 1600,
  },
  {
    prefix: '',
    value: 48,
    suffix: '',
    label: 'Países disponibles',
    icon: 'ri-global-line',
    description: 'Envío internacional con seguimiento en tiempo real incluido',
    duration: 2000,
  },
  {
    prefix: '<',
    value: 3,
    suffix: 'mm',
    label: 'Grosor del anillo',
    icon: 'ri-circle-line',
    description: 'El perfil más fino del mercado. Apenas lo notarás puesto',
    duration: 1400,
  },
  {
    prefix: '',
    value: 30,
    suffix: 's',
    label: 'Medición SpO2',
    icon: 'ri-lungs-line',
    description: 'Lectura de saturación de oxígeno en menos de medio minuto',
    duration: 1600,
  },
];

const MetricsSection = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="metricas" className="py-28 w-full bg-stone-950 relative overflow-hidden">
      {/* Decorative ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[200px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center max-w-2xl mx-auto mb-20 scroll-hidden ${titleVisible ? 'scroll-visible' : ''}`}
        >
          <span className="font-body text-xs font-semibold tracking-widest text-amber-500 uppercase block mb-4">
            Números reales
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-white leading-tight mb-5">
            La tecnología detrás
            <br />
            <em className="not-italic font-semibold text-gradient">del anillo.</em>
          </h2>
          <p className="font-body text-white/50 text-base leading-relaxed">
            No son promesas. Son datos verificados, medidos y actualizados en
            tiempo real por millones de usuarios en todo el mundo.
          </p>
        </div>

        {/* Metrics grid */}
        <div
          ref={cardsRef}
          className={`grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6 scroll-hidden ${cardsVisible ? 'scroll-visible' : ''}`}
        >
          {metrics.map((m, i) => (
            <div
              key={m.label}
              className={`scroll-hidden ${cardsVisible ? 'scroll-visible' : ''}`}
              style={{ transitionDelay: cardsVisible ? `${i * 0.08}s` : '0s' }}
            >
              <MetricCard {...m} />
            </div>
          ))}
        </div>

        {/* Bottom strip */}
        <div
          className={`mt-16 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 scroll-hidden ${titleVisible ? 'scroll-visible delay-400' : ''}`}
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-600/20 rounded-full border border-amber-500/30">
              <i className="ri-shield-check-line text-amber-400 text-lg" />
            </div>
            <div>
              <div className="font-body text-sm font-semibold text-white">
                Validación clínica independiente
              </div>
              <div className="font-body text-xs text-white/40">
                Estudio con 3,200 participantes · Publicado en 2024
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-emerald-600/20 rounded-full border border-emerald-500/30">
              <i className="ri-award-line text-emerald-400 text-lg" />
            </div>
            <div>
              <div className="font-body text-sm font-semibold text-white">
                Premio Innovación Wearable 2024
              </div>
              <div className="font-body text-xs text-white/40">
                CES Innovation Awards · Mejor dispositivo de salud
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-rose-600/20 rounded-full border border-rose-500/30">
              <i className="ri-heart-fill text-rose-400 text-lg" />
            </div>
            <div>
              <div className="font-body text-sm font-semibold text-white">
                98% de satisfacción de usuarios
              </div>
              <div className="font-body text-xs text-white/40">
                Encuesta a 12,000 clientes · NPS de +72
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MetricsSection;
